import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Clock, 
  MapPin, 
  Edit, 
  Trash2, 
  Copy,
  ArrowLeft,
  Users,
  Star,
  Briefcase,
  ListChecks,
  ChevronRight,
  Plus,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { activityService } from '@/lib/api/activity.service';
import { eventService } from '@/lib/api/event.service';
import { celebrationService } from '@/lib/api/celebration.service';
import { ActivityPeopleManager } from './ActivityPeopleManager';
import { ActivityItemsList } from './ActivityItemsList';
import { formatActivityTimeRange } from '@/utils/dateFormat';
import type { ActivityWithAssociations } from '@/types/activity.types';
import type { Celebration } from '@/types/celebration.types';
import type { Event } from '@/types/event.types';

interface ActivityDetailsProps {
  activityId: number;
  canEdit: boolean;
}

interface ErrorResponse {
  response?: {
    data?: {
      message?: string;
    };
  };
}

export const ActivityDetails: React.FC<ActivityDetailsProps> = ({ 
  activityId, 
  canEdit
}) => {
  const { t } = useTranslation(['activities', 'common']);
  const navigate = useNavigate();
  const [activity, setActivity] = useState<ActivityWithAssociations | null>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [celebration, setCelebration] = useState<Celebration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await activityService.getActivity(activityId);
      setActivity(data);

      // Load event and celebration info for breadcrumb
      if (data.event_id) {
        try {
          const eventData = await eventService.getEvent(data.event_id);
          setEvent(eventData);
          if (eventData.celebration_id) {
            const celebrationData = await celebrationService.getCelebration(eventData.celebration_id);
            setCelebration(celebrationData);
          }
        } catch (err) {
          console.error('Failed to load event/celebration:', err);
        }
      }
    } catch (err: unknown) {
      const message = (err as ErrorResponse)?.response?.data?.message || t('activities:details.errors.loadActivity');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [activityId, t]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const handleDelete = async () => {
    if (!confirm(t('activities:details.confirmDelete'))) return;

    setDeleting(true);
    try {
      await activityService.deleteActivity(activityId);
      navigate(`/events/${activity?.event_id}`);
    } catch (err: unknown) {
      const message = (err as ErrorResponse)?.response?.data?.message || t('activities:details.errors.delete');
      setError(message);
      setDeleting(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-600';
      case 'planned': return 'bg-blue-100 text-blue-700';
      case 'published': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="h-5 w-5 animate-pulse" />
          <span>{t('activities:details.loading')}</span>
        </div>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
        {error || t('activities:details.errors.notFound')}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Breadcrumb with celebration name */}
      <nav className="flex items-center text-xs text-muted-foreground">
        <Link to="/" className="hover:text-primary">{t('common:nav.home')}</Link>
        <ChevronRight className="h-3 w-3 mx-1" />
        {celebration && (
          <>
            <Link 
              to={`/celebrations/${celebration.id}`} 
              className="hover:text-primary truncate max-w-[120px]"
              title={celebration.title}
            >
              {celebration.title}
            </Link>
            <ChevronRight className="h-3 w-3 mx-1" />
          </>
        )}
        {event && (
          <>
            <Link 
              to={`/events/${event.id}`} 
              className="hover:text-primary truncate max-w-[120px]"
              title={event.title}
            >
              {event.title}
            </Link>
            <ChevronRight className="h-3 w-3 mx-1" />
          </>
        )}
        <span className="font-medium text-foreground truncate max-w-[150px]">{activity.title}</span>
      </nav>

      {/* Compact Header */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(`/events/${activity.event_id}`)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-xl font-bold truncate">{activity.title}</h1>
              <Badge className={getStatusColor(activity.status)}>{activity.status}</Badge>
            </div>

            {/* Time and Location */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground ml-10">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatActivityTimeRange(activity.start_time, activity.end_time)}
              </span>
              {activity.location && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {activity.location}
                </span>
              )}
            </div>

            {activity.description && (
              <p className="text-xs text-muted-foreground mt-2 ml-10 line-clamp-2">{activity.description}</p>
            )}
          </div>

          {/* Actions */}
          {canEdit && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button variant="ghost" size="icon-sm" onClick={() => navigate(`/activities/${activityId}/duplicate`)} title={t('common:actions.copy')}>
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={() => navigate(`/events/${activity.event_id}/activities/${activityId}/edit`)} title={t('common:actions.edit')}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={handleDelete} disabled={deleting} className="text-destructive hover:bg-destructive/10" title={t('common:actions.delete')}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Timeline View for Sub-Activities */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">{t('activities:details.timeline')}</span>
            <Badge variant="secondary" size="sm">
              {(activity.sub_activities?.length || 0) + 1}
            </Badge>
          </div>
          {canEdit && (
            <Button size="sm" onClick={() => navigate(`/activities/${activityId}/sub-activities/new`)}>
              <Plus className="h-3 w-3 mr-1" />
              {t('common:actions.add')}
            </Button>
          )}
        </div>

        <div className="p-4">
          {/* Timeline */}
          <div className="relative">
            {/* Main Activity - Parent */}
            <div className="flex gap-3">
              {/* Timeline line and dot */}
              <div className="flex flex-col items-center">
                <div className="w-3 h-3 rounded-full bg-primary border-2 border-white shadow-sm flex-shrink-0 z-10" />
                {activity.sub_activities && activity.sub_activities.length > 0 && (
                  <div className="w-0.5 bg-gray-200 flex-1 min-h-[20px]" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">{activity.title}</span>
                  <Badge className={getStatusColor(activity.status)} size="sm">{activity.status}</Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="font-mono">{formatActivityTimeRange(activity.start_time, activity.end_time)}</span>
                  {activity.location && <span>📍 {activity.location}</span>}
                </div>
              </div>
            </div>

            {/* Sub-Activities - Children */}
            {activity.sub_activities && activity.sub_activities.length > 0 && (
              <div className="ml-1.5">
                {activity.sub_activities.map((subActivity, index) => {
                  const isLast = index === activity.sub_activities!.length - 1;
                  // Use parent times if sub-activity times are null
                  const displayStartTime = subActivity.start_time || activity.start_time;
                  const displayEndTime = subActivity.end_time || activity.end_time;
                  const inheritsTime = !subActivity.start_time;
                  return (
                    <div key={subActivity.id} className="flex gap-3">
                      {/* Timeline line and dot */}
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-gray-400 border-2 border-white shadow-sm flex-shrink-0 z-10" />
                        {!isLast && <div className="w-0.5 bg-gray-200 flex-1 min-h-[20px]" />}
                      </div>

                      {/* Content */}
                      <div 
                        className="flex-1 pb-3 cursor-pointer hover:bg-muted/30 -ml-1 pl-1 rounded transition-colors"
                        onClick={() => navigate(`/activities/${subActivity.id}`)}
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-medium text-sm text-muted-foreground">{subActivity.title}</span>
                          <Badge className={getStatusColor(subActivity.status)} size="sm">{subActivity.status}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className={`font-mono ${inheritsTime ? 'italic opacity-70' : ''}`}>
                            {formatActivityTimeRange(displayStartTime, displayEndTime)}
                            {inheritsTime && t('activities:details.inheritedTime')}
                          </span>
                          {subActivity.location && <span>📍 {subActivity.location}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty state for sub-activities */}
            {(!activity.sub_activities || activity.sub_activities.length === 0) && (
              <p className="text-xs text-muted-foreground ml-6 mt-2">{t('activities:details.noSubActivities')}</p>
            )}
          </div>
        </div>
      </div>

      {/* People & Vendors - Combined 3-column card */}
      {canEdit && (
        <>
          <ActivityPeopleManager activity={activity} onUpdate={loadActivity} />
          <ActivityItemsList activity={activity} onUpdate={loadActivity} />
        </>
      )}

      {/* Read-only sections for non-editors */}
      {!canEdit && (
        <div className="space-y-4">
          {/* Leads */}
          {activity.leads && activity.leads.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                <Users className="h-4 w-4 text-primary" />
                {t('activities:details.sections.leads')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {activity.leads.map((lead) => (
                  <div key={lead.id} className="inline-flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded-full text-xs">
                    <span className="font-medium">{lead.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key Participants */}
          {activity.key_participants && activity.key_participants.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                <Star className="h-4 w-4 text-yellow-500" />
                {t('activities:details.sections.keyParticipants')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {activity.key_participants.map((p) => (
                  <div key={p.guest_id} className="inline-flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded-full text-xs">
                    <span className="font-medium">{p.guest.name}</span>
                    {p.role_description && <span className="text-muted-foreground">· {p.role_description}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vendors */}
          {activity.vendors && activity.vendors.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                <Briefcase className="h-4 w-4 text-blue-500" />
                {t('activities:details.sections.vendors')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {activity.vendors.map((v) => (
                  <div key={v.id} className="inline-flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded-full text-xs">
                    <span className="font-medium">{v.name}</span>
                    {v.service_type && <span className="text-muted-foreground">· {v.service_type}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Items */}
          {activity.items && activity.items.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                <ListChecks className="h-4 w-4 text-primary" />
                {t('activities:details.sections.items')}
              </h3>
              <div className="space-y-1">
                {activity.items.map((item, idx) => (
                  <div key={item.id} className="flex items-start gap-2 text-sm">
                    <span className="text-muted-foreground">{idx + 1}.</span>
                    <span>{item.content}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
